import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";

// ---------------------------------------------------------------------------
// Global session-timeout warning + auto-logout.
//
// Backend refresh-token hard-expiry (single shared backend, confirmed) is
// 6 hours from login. We warn well before that real cutoff so the user has
// a chance to save work, then force logout with a 5 minute safety buffer
// before the token would actually die (we never want an API call to
// silently 401 because the real session outlived our own timer).
// ---------------------------------------------------------------------------
const LOGIN_FLAG_KEY = "login";
const LOGIN_TIME_KEY = "loginTime";

const WARNING_AFTER_MS = 5 * 60 * 60 * 1000 + 45 * 60 * 1000; // 5h45m
const COUNTDOWN_MS = 10 * 60 * 1000; // 10min
const LOGOUT_AFTER_MS = WARNING_AFTER_MS + COUNTDOWN_MS; // 5h55m (5min buffer before the real 6h cutoff)
const CHECK_INTERVAL_MS = 1000;

// Every KSDPL panel (RM, AIC, CH, HR...) writes the same "login"/"loginTime"
// keys. On live they sit on separate subdomains (separate localStorage), but
// in local dev they all share localhost:3000 — so a session only counts here
// if it was started from THIS panel's Login page.
export const PANEL_KEY = "panel";
export const PANEL_NAME = "RM";

// True while this panel's login is still inside the auto-logout window —
// used by Login.jsx to skip the login form (and MFA) when the tab is
// reopened, and by useAuthGuard to protect every other screen.
export function hasActiveSession() {
  if (localStorage.getItem(LOGIN_FLAG_KEY) !== "true") return false;
  if (localStorage.getItem(PANEL_KEY) !== PANEL_NAME) return false;
  const loginTime = Number(localStorage.getItem(LOGIN_TIME_KEY));
  return Boolean(loginTime) && Date.now() - loginTime < LOGOUT_AFTER_MS;
}

function formatCountdown(ms) {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

const SessionTimeoutWatcher = () => {
  const navigate = useNavigate();
  const dismissedRef = useRef(false);
  const [showModal, setShowModal] = useState(false);
  const [remainingMs, setRemainingMs] = useState(COUNTDOWN_MS);
  const [logoutAt, setLogoutAt] = useState(null);

  useEffect(() => {
    const forceLogout = () => {
      localStorage.clear();
      dismissedRef.current = false;
      setShowModal(false);
      navigate("/");
    };

    const tick = () => {
      const isLoggedIn =
        localStorage.getItem(LOGIN_FLAG_KEY) === "true" &&
        localStorage.getItem(PANEL_KEY) === PANEL_NAME;
      if (!isLoggedIn) {
        dismissedRef.current = false;
        setShowModal(false);
        return;
      }

      let loginTime = Number(localStorage.getItem(LOGIN_TIME_KEY));
      if (!loginTime) {
        // Edge case: session started before this feature existed.
        loginTime = Date.now();
        localStorage.setItem(LOGIN_TIME_KEY, String(loginTime));
      }

      const elapsed = Date.now() - loginTime;

      if (elapsed >= LOGOUT_AFTER_MS) {
        forceLogout();
      } else if (elapsed >= WARNING_AFTER_MS) {
        setRemainingMs(LOGOUT_AFTER_MS - elapsed);
        setLogoutAt(new Date(loginTime + LOGOUT_AFTER_MS));
        if (!dismissedRef.current) {
          setShowModal(true);
        }
      } else {
        setShowModal(false);
      }
    };

    tick();
    const intervalId = setInterval(tick, CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [navigate]);

  const handleDismiss = () => {
    dismissedRef.current = true;
    setShowModal(false);
  };

  const handleLogoutNow = () => {
    localStorage.clear();
    dismissedRef.current = false;
    setShowModal(false);
    navigate("/");
  };

  return (
    <Dialog open={showModal} onClose={handleDismiss} maxWidth="xs" fullWidth>
      <DialogTitle>Your session is about to expire</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          For your security, you will be automatically logged out in{" "}
          <strong>{formatCountdown(remainingMs)}</strong>.
        </Typography>
        {logoutAt && (
          <Typography variant="body2" color="text.secondary">
            Logout at {logoutAt.toLocaleTimeString()}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDismiss}>Dismiss</Button>
        <Button onClick={handleLogoutNow} variant="contained" color="error">
          Logout Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutWatcher;
